import { store } from 'redux/store';
import { State } from 'redux/initialState';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { FC, ReactNode, createContext, useContext, useState } from 'react';

import { setModal } from 'modules/_shared/redux/modalActions';
import { useGetHoldingClasses } from 'modules/partners/hooks/useGetHoldingClasses';
import { useGetActualSociety } from 'hooks/useGetActualSociety';
import { useGetMenu } from 'modules/administratives/hooks/useGetMenu';
import { AddHoldingClassModalProvider } from 'modules/_shared/components/Modals/AddHoldingClassModal/context/AddHoldingClassModalContext';
import AddHoldingClassModal from 'modules/_shared/components/Modals/AddHoldingClassModal';
import actionTypes from 'constants/actionTypes';
import { holdingClassListPageInitValue } from '../constants/holdingClassListPageInitValue';
import { HoldingClassListPageContextType } from '../types/HoldingClassListPageContextType';

const HoldingClassListPageContext =
  createContext<HoldingClassListPageContextType>(holdingClassListPageInitValue);

export const HoldingClassListPageProvider: FC<{
  children: ReactNode;
}> = ({ children }) => {
  const { societyId } = useParams();

  const actualSociety = useSelector(
    (state: State) => state.society?.actualSociety
  );

  const holdingClasses = useSelector(
    (state: State) => state.holdingClasses || []
  );

  const [page, setPage] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);

  useGetActualSociety(societyId);
  useGetMenu();

  const { isLoading } = useGetHoldingClasses({
    societyId,
    page,
    rowsPerPage,
  });

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handleAddClass = () => {
    store.dispatch(
      setModal(
        <AddHoldingClassModalProvider modalActionType={actionTypes.ADD}>
          <AddHoldingClassModal />
        </AddHoldingClassModalProvider>
      )
    );
  };

  const handleClassAction = (index: number, actionType: string) => {
    const holdingClass = holdingClasses[index];
    if (holdingClass) {
      store.dispatch(
        setModal(
          <AddHoldingClassModalProvider
            modalActionType={actionType}
            holdingClassToEdit={holdingClass}
          >
            <AddHoldingClassModal />
          </AddHoldingClassModalProvider>
        )
      );
    }
  };

  const handleViewClass = (index: number) =>
    handleClassAction(index, actionTypes.VIEW);

  const handleEditClass = (index: number) =>
    handleClassAction(index, actionTypes.EDIT);

  return (
    <HoldingClassListPageContext.Provider
      value={{
        data: {
          societyName: actualSociety?.name || '',
          societyNominalValue: actualSociety?.nominalValue || 0,
          holdingClassesTotal: actualSociety?.holdingClassesCount?.total || 0,
          holdingClasses,
          page,
          rowsPerPage,
          isLoading,
        },
        actions: {
          handleChangePage,
          handleChangeRowsPerPage,
          handleAddClass,
          handleViewClass,
          handleEditClass,
        },
      }}
    >
      {children}
    </HoldingClassListPageContext.Provider>
  );
};

export const useHoldingClassListPage = () => {
  const context = useContext(HoldingClassListPageContext);
  if (!context) {
    throw new Error(
      'useHoldingClassListPage must be used within a HoldingClassListPageProvider'
    );
  }
  return context;
};
